import {
  API_PREFIX,
  paginatedFromDto,
  paginationMetaDtoSchema,
  type HttpClient,
} from "@nasam/kernel";
import { z } from "zod";

import { BULK_STATUSES } from "../../../shared/bulkUpload";
import {
  DEFAULT_GEOFENCE_COLOR,
  GEOFENCE_COLORS,
  GEOFENCE_COLOR_VALUES,
  type CreateGeofenceInput,
  type Geofence,
  type GeofenceBulkRow,
  type GeofenceColor,
  type GeofenceListQuery,
  type GeofenceMapQuery,
  type GeofenceShape,
  type GeofencesRepository,
  type UpdateGeofenceInput,
} from "../domain";

const GEOFENCES_PATH = `${API_PREFIX}/geofences`;
const GEOFENCES_MAP_PATH = `${GEOFENCES_PATH}/map`;
const GEOFENCES_BULK_PATH = `${GEOFENCES_PATH}/bulk-upload`;
const GEOFENCES_TEMPLATE_PATH = `${GEOFENCES_BULK_PATH}/template`;
const GEOFENCES_BULK_PREVIEW_PATH = `${GEOFENCES_BULK_PATH}/preview`;
const GEOFENCES_BULK_ERROR_REPORT_PATH = `${GEOFENCES_BULK_PATH}/error-report`;
const GEOFENCES_EXPORT_PATH = `${GEOFENCES_PATH}/export`;

const pointDtoSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

// A POLYGON has its `boundary` and null center and radius; a CIRCLE the
// reverse.
const geofenceDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(["POLYGON", "CIRCLE"]),
  color: z.string(),
  boundary: z.array(pointDtoSchema).nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  radiusMeters: z.number().nullish(),
  accountId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createResponseSchema = z.object({
  message: z.string().optional(),
  data: geofenceDtoSchema,
});

// Rows also name their `account`, which the screen doesn't show (a client
// user only sees their own company's geofences).
const listResponseSchema = z.object({
  data: z.array(geofenceDtoSchema),
  meta: paginationMetaDtoSchema,
});

const mapResponseSchema = z.object({
  data: z.array(geofenceDtoSchema),
});

const detailResponseSchema = z.object({
  data: geofenceDtoSchema,
});

const deleteResponseSchema = z.object({
  message: z.string().optional(),
});

// A row's values are as read from the sheet: an invalid row may carry a
// coordinate or radius that isn't a number.
const bulkCellSchema = z.union([z.number(), z.string()]).nullish();

const bulkRowDtoSchema = z.object({
  row: z.number(),
  name: z.string().nullish(),
  latitude: bulkCellSchema,
  longitude: bulkCellSchema,
  radiusMeters: bulkCellSchema,
  status: z.enum(BULK_STATUSES),
  errors: z.array(z.string()).default([]),
});

const bulkPreviewResponseSchema = z.object({
  data: z.object({
    totalRecords: z.number(),
    validRecords: z.number(),
    invalidRecords: z.number(),
    duplicateRecords: z.number(),
    rows: z.array(bulkRowDtoSchema),
  }),
});

const bulkImportResponseSchema = z.object({
  message: z.string().optional(),
  data: z.object({
    totalRecords: z.number(),
    createdRecords: z.number(),
    failedRecords: z.number(),
    failedRows: z.array(bulkRowDtoSchema).default([]),
  }),
});

/** The sheet's `radius` column comes back as `radiusMeters`. */
function bulkRowFromDto(
  dto: z.infer<typeof bulkRowDtoSchema>,
): GeofenceBulkRow {
  return {
    row: dto.row,
    status: dto.status,
    errors: dto.errors,
    name: dto.name ?? null,
    latitude: dto.latitude ?? null,
    longitude: dto.longitude ?? null,
    radius: dto.radiusMeters ?? null,
  };
}

/** Bulk Upload's body: the sheet in the `file` field. */
function bulkBody(file: File): FormData {
  const body = new FormData();
  body.set("file", file, file.name);
  return body;
}

/** `search` only when there is one: an empty one is no search at all. */
function searchParam(search: string | undefined) {
  const trimmed = search?.trim();
  return trimmed === undefined || trimmed === "" ? {} : { search: trimmed };
}

function listParams(query: GeofenceListQuery) {
  return {
    page: query.page,
    limit: query.limit,
    // Newest first, as a list just added to reads.
    sortBy: "createdAt",
    sort: "desc",
    ...searchParam(query.search),
    ...(query.accountId === undefined ? {} : { accountId: query.accountId }),
  };
}

/** The API keeps any `#RRGGBB`; the screens offer eight colors. One outside
 *  them shows in the default. */
function colorFromDto(hex: string): GeofenceColor {
  const value = hex.toLowerCase();
  return (
    GEOFENCE_COLORS.find((color) => GEOFENCE_COLOR_VALUES[color] === value) ??
    DEFAULT_GEOFENCE_COLOR
  );
}

function shapeFromDto(dto: z.infer<typeof geofenceDtoSchema>): GeofenceShape {
  if (dto.type === "POLYGON") {
    return { kind: "POLYGON", points: dto.boundary ?? [] };
  }
  return {
    kind: "CIRCLE",
    center: { latitude: dto.latitude ?? 0, longitude: dto.longitude ?? 0 },
    radiusMeters: dto.radiusMeters ?? 0,
  };
}

function geofenceFromDto(dto: z.infer<typeof geofenceDtoSchema>): Geofence {
  return {
    id: dto.id,
    // TEMPORARY: the API has no code yet; the card and details hide an empty one.
    code: "",
    name: dto.name,
    color: colorFromDto(dto.color),
    shape: shapeFromDto(dto),
  };
}

/** The body: a polygon sends its boundary, a circle its center and radius.
 *  Create always sends the color, since the API's own default is blue; an edit
 *  only one the user picked (left out, the API keeps the stored one). */
function geofenceBody(input: CreateGeofenceInput | UpdateGeofenceInput) {
  const common = {
    name: input.name.trim(),
    ...(input.color === undefined
      ? {}
      : { color: GEOFENCE_COLOR_VALUES[input.color].toUpperCase() }),
  };
  const { shape } = input;
  return shape.kind === "POLYGON"
    ? { ...common, type: "POLYGON", boundary: shape.points }
    : {
        ...common,
        type: "CIRCLE",
        latitude: shape.center.latitude,
        longitude: shape.center.longitude,
        radiusMeters: shape.radiusMeters,
      };
}

/** Geofences on the API: the list (`GET /geofences`), the map layer
 *  (`GET /geofences/map`), details, edit and delete (`/geofences/:id`),
 *  create (`POST /geofences`), the Excel export (`GET /geofences/export`) and
 *  Bulk Upload (`/geofences/bulk-upload/...`). */
export function createGeofencesHttpRepository(
  http: HttpClient,
): GeofencesRepository {
  return {
    async list(query) {
      const response = listResponseSchema.parse(
        await http.get(GEOFENCES_PATH, { params: listParams(query) }),
      );
      return paginatedFromDto(response, geofenceFromDto);
    },
    // Not paged, ordered by name.
    async listForMap(query: GeofenceMapQuery) {
      const response = mapResponseSchema.parse(
        await http.get(GEOFENCES_MAP_PATH, {
          params: searchParam(query.search),
        }),
      );
      return response.data.map(geofenceFromDto);
    },
    // 404 ("Geofence not found.", also for another company's) propagates as
    // HttpError; the screen shows its not-found state.
    async getById(id) {
      const response = detailResponseSchema.parse(
        await http.get(`${GEOFENCES_PATH}/${encodeURIComponent(id)}`),
      );
      return geofenceFromDto(response.data);
    },
    // 409 (the name is taken), 400 and 403 propagate as HttpError with the
    // backend's translated message.
    async create(input) {
      const response = createResponseSchema.parse(
        await http.post(GEOFENCES_PATH, geofenceBody(input)),
      );
      return {
        geofence: geofenceFromDto(response.data),
        ...(response.message === undefined
          ? {}
          : { message: response.message }),
      };
    },
    async exportGeofences(query) {
      const file = await http.get<unknown>(GEOFENCES_EXPORT_PATH, {
        // In the list's order: newest first.
        params: {
          sortBy: "createdAt",
          sort: "desc",
          ...searchParam(query.search),
        },
        responseType: "blob",
      });
      if (!(file instanceof Blob)) {
        throw new Error("The geofence export didn't come back as a file.");
      }
      return file;
    },
    async downloadTemplate() {
      const file = await http.get<unknown>(GEOFENCES_TEMPLATE_PATH, {
        responseType: "blob",
      });
      if (!(file instanceof Blob)) {
        throw new Error("The geofence template didn't come back as a file.");
      }
      return file;
    },
    // A file refused as a whole (400 wrong template, missing columns, no or
    // too many records; 413 too large; 415 not .xlsx) propagates as HttpError
    // with the backend's translated message.
    async previewBulkUpload(file) {
      const { data } = bulkPreviewResponseSchema.parse(
        await http.post(GEOFENCES_BULK_PREVIEW_PATH, bulkBody(file)),
      );
      return { ...data, rows: data.rows.map(bulkRowFromDto) };
    },
    // As the preview's, plus 400 when no row of the file can be imported.
    // Every geofence imported is a circle in the API's default color.
    async bulkUpload(file) {
      const response = bulkImportResponseSchema.parse(
        await http.post(GEOFENCES_BULK_PATH, bulkBody(file)),
      );
      return {
        ...response.data,
        failedRows: response.data.failedRows.map(bulkRowFromDto),
        ...(response.message === undefined
          ? {}
          : { message: response.message }),
      };
    },
    async downloadBulkErrorReport(file) {
      const report = await http.post<unknown>(
        GEOFENCES_BULK_ERROR_REPORT_PATH,
        bulkBody(file),
        { responseType: "blob" },
      );
      if (!(report instanceof Blob)) {
        throw new Error("The geofence error report didn't come back as a file.");
      }
      return report;
    },
    // 409 (another geofence has the name; its own isn't a clash), 404 (gone,
    // or another company's), 400 and 500 propagate as HttpError, like
    // create's.
    async update(id, input) {
      const response = createResponseSchema.parse(
        await http.patch(
          `${GEOFENCES_PATH}/${encodeURIComponent(id)}`,
          geofenceBody(input),
        ),
      );
      return {
        geofence: geofenceFromDto(response.data),
        ...(response.message === undefined
          ? {}
          : { message: response.message }),
      };
    },
    // 404 ("Geofence not found.", already deleted or another company's)
    // propagates as HttpError.
    async delete(id) {
      const response = deleteResponseSchema.parse(
        await http.delete(`${GEOFENCES_PATH}/${encodeURIComponent(id)}`),
      );
      return response.message === undefined
        ? {}
        : { message: response.message };
    },
  };
}
